import React, { useEffect, useState } from 'react'
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material'

import AddCircleIcon from '@mui/icons-material/AddCircle'
import DeleteIcon from '@mui/icons-material/Delete'

import { getDynamicLightingThresholdOptions } from '../../../helpers/dynamicLightingThresholds'

const isEmpty = value => value === '' || value === undefined || value === null

const isInvalidNumber = value => isEmpty(value) || Number.isNaN(Number(value)) || Number(value) < 0

// El máximo es opcional (vacío = sin límite), pero si se rellena no puede ser menor que el mínimo
const isInvalidMax = lightLevel => (
  !isEmpty(lightLevel.max) &&
  (isInvalidNumber(lightLevel.max) || Number(lightLevel.max) < Number(lightLevel.min))
)

export const ThresholdsEditor = ({ title, thresholds, onSave }) => {

  const [lightLevels, setLightLevels] = useState([])
  useEffect(() => {
    console.log(lightLevels)
  }, [lightLevels])

  useEffect(() => {
    setLightLevels(
      getDynamicLightingThresholdOptions(thresholds).map(option => ({
        ...option,
        value: String(option.value),
        min: String(option.min ?? ''),
        max: String(option.max ?? '')
      }))
    )
  }, [thresholds])

  // Cambia un campo (label o value) de la fila que se ha editado
  const handleChange = (rowIndex, field, newValue) => {
    const updatedLightLevels = lightLevels.map((lightLevel, index) => {
      const isEditedRow = index === rowIndex

      if (isEditedRow) {
        return { ...lightLevel, [field]: newValue }
      }

      return lightLevel
    })

    setLightLevels(updatedLightLevels)
  }

  const handleAdd = () => {
    setLightLevels([...lightLevels, { key: `level_${Date.now()}`, label: '', value: '', min: '', max: '' }])
  }

  const handleDelete = index => {
    setLightLevels(lightLevels.filter((_, i) => i !== index))
  }

  const hasInvalid = lightLevels.length === 0 || lightLevels.some(lightLevel => (
    !lightLevel.label.trim() ||
    isInvalidNumber(lightLevel.value) ||
    isInvalidNumber(lightLevel.min) ||
    isInvalidMax(lightLevel)
  ))

  const handleSave = () => {
    onSave(lightLevels.map(lightLevel => {
      const { max, ...rest } = lightLevel
      const savedLightLevel = {
        ...rest,
        label: lightLevel.label.trim(),
        value: Number(lightLevel.value),
        min: Number(lightLevel.min)
      }

      // Sin máximo = nivel sin límite superior
      return isEmpty(max) ? savedLightLevel : { ...savedLightLevel, max: Number(max) }
    }))
  }

  return (
    <Card>
      <CardHeader
        title={title}
        action={
          <Tooltip title={'Añadir nivel'} aria-label='add'>
            <IconButton onClick={handleAdd}>
              <AddCircleIcon />
            </IconButton>
          </Tooltip>
        }
      />
      <CardContent>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Nivel de luminosidad</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell>Mínimo</TableCell>
              <TableCell>Máximo</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {lightLevels.map((lightLevel, index) => (
              <TableRow key={lightLevel.key}>
                <TableCell>
                  <TextField
                    size='small'
                    fullWidth
                    value={lightLevel.label}
                    error={!lightLevel.label.trim()}
                    onChange={e => handleChange(index, 'label', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size='small'
                    type='number'
                    value={lightLevel.value}
                    error={isInvalidNumber(lightLevel.value)}
                    inputProps={{ min: 0 }}
                    onChange={e => handleChange(index, 'value', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size='small'
                    type='number'
                    value={lightLevel.min}
                    error={isInvalidNumber(lightLevel.min)}
                    inputProps={{ min: 0 }}
                    onChange={e => handleChange(index, 'min', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size='small'
                    type='number'
                    value={lightLevel.max}
                    placeholder='Sin límite'
                    error={isInvalidMax(lightLevel)}
                    inputProps={{ min: 0 }}
                    onChange={e => handleChange(index, 'max', e.target.value)}
                  />
                </TableCell>
                <TableCell align='right'>
                  <Tooltip title={'Eliminar nivel'} aria-label='delete'>
                    <IconButton onClick={() => handleDelete(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
        <Button variant='contained' disabled={hasInvalid} onClick={handleSave}>
          Guardar
        </Button>
      </CardActions>
    </Card>
  )
}
